import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, InputNumber, Popconfirm, Select, Space, Table } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '~/services/api';
import history from '~/services/history';

import { Container } from './styles';

export default function NewInternalOrder() {
  const [form] = Form.useForm();
  const { TextArea } = Input;

  const [productOrderList, setProductOrderList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [technicianList, setTechnicianList] = useState([]);

  useEffect(() => {
    async function getUser() {
      const { data: userData } = await api.get('users');
      const list = userData?.map((user) => ({ value: user.id, label: user.name }));
      setUserList(list);

      const { data: technicianData } = await api.get('technicians?list=true');
      const technicians = technicianData?.map((technician) => ({ value: technician.id, label: technician.name }));
      setTechnicianList(technicians);
    }
    getUser();
  }, []);

  const handleAddProduct = useCallback(() => {
    const { productTitleRef, productQtyRef, manualNotes } = form.getFieldsValue();

    if (!productTitleRef || !productQtyRef || !manualNotes) {
      toast.error('Please enter information for product description, quantity and notes.');
      return;
    }

    const newProductOrder = {
      title: productTitleRef,
      quantity: productQtyRef,
      notes: manualNotes,
    };

    form.setFieldsValue({ productTitleRef: '' });
    form.setFieldsValue({ productQtyRef: '' });
    form.setFieldsValue({ manualNotes: '' });
    setProductOrderList([...productOrderList, newProductOrder]);
  }, [form, productOrderList]);

  const handleRemoveProduct = useCallback(
    (index) => {
      const newList = [...productOrderList];
      newList.splice(index, 1);
      setProductOrderList(newList);
    },
    [productOrderList]
  );

  const onFinish = async (values) => {
    if (productOrderList.length > 0) {
      const newInternalOrder = {
        requested_by_id: values.requestedBy,
        technician_id: values.technician_id,
        notes: values.notes,
      };

      const internal_order_id = await api.post('internal_orders', newInternalOrder);

      productOrderList.map(async (product) => {
        const newInternalOrderItem = {
          internal_order_id: internal_order_id.data,
          quantity: product.quantity,
          title: product.title,
          notes: product.notes,
        };

        const result = await api.post('internal_order_items', newInternalOrderItem);
        if (result.data.error) {
          toast.error(`${result.data.error}`);
        } else {
          toast.success('Internal Order created successfully');
          history.push('/internalOrders');
        }
      });
    } else {
      toast.error('Please enter at least one item');
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      render: (_text, _row, index) => index + 1,
    },
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
    },
    {
      title: 'Manual Notes',
      dataIndex: 'notes',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_, record) => (
        <Popconfirm
          onConfirm={() => handleRemoveProduct(record)}
          title="Are you sure you want to delete?"
          trigger="click"
          placement="left"
          okText="Yes"
          cancelText="No"
        >
          <Button type="primary" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Container>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Link to="/internalOrders">
            <Button type="default" icon={<ArrowLeftOutlined />} size="large">
              Back
            </Button>
          </Link>
          <span style={{ fontWeight: 600, fontSize: 22, color: '#3b3b3b' }}>New Internal Order</span>
          <div />
        </div>
        <Form form={form} name="nest-messages" onFinish={onFinish} layout="vertical" style={{ width: '100%' }}>
          <Card
            title={<span style={{ fontWeight: 500, fontSize: 18 }}>Order Info</span>}
            headStyle={{ backgroundColor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}
            style={{ marginBottom: 32, width: '100%' }}
            bodyStyle={{ padding: 24 }}
          >
            <Space
              size={32}
              // style={{ width: '100%', flexWrap: 'wrap' }}
            >
              <Form.Item
                name="requestedBy"
                label="Requested By"
                rules={[{ required: true, message: 'Requested By is required.' }]}
                style={{ minWidth: 400, flex: 1 }}
              >
                <Select
                  options={userList}
                  allowClear
                  style={{ width: '100%' }}
                  showSearch
                  placeholder="Select requester"
                  optionFilterProp="label"
                  filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item
                name="technician_id"
                label="Technician"
                rules={[{ required: true, message: 'Technician Assigned is required.' }]}
                style={{ minWidth: 400, flex: 1 }}
              >
                <Select
                  options={technicianList}
                  allowClear
                  style={{ width: '100%' }}
                  showSearch
                  placeholder="Select technician"
                  optionFilterProp="label"
                  filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item hasFeedback name="notes" label="Internal Order Notes" style={{ minWidth: 400, flex: 1 }}>
                <TextArea
                  showCount
                  maxLength={2000}
                  allowClear
                  style={{ width: 400 }}
                  placeholder="Add reason for this order, if it's related to a listing, etc."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
            </Space>
          </Card>
          <Card
            title={<span style={{ fontWeight: 500, fontSize: 18 }}>Product Info</span>}
            headStyle={{ backgroundColor: '#f5f7fa', borderBottom: '1px solid #e0e0e0' }}
            style={{ marginBottom: 32, width: '100%' }}
            bodyStyle={{ padding: 24 }}
          >
            <Space style={{ width: '100%', flexWrap: 'wrap' }}>
              <Form.Item name="productTitleRef" required label="Product description" style={{ minWidth: 400, flex: 2 }}>
                <Input placeholder="Product description" allowClear style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="productQtyRef" required label="Quantity" style={{ minWidth: 360, flex: 1 }}>
                <InputNumber type="number" min={0} max={99999} placeholder="Qty" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item hasFeedback name="manualNotes" required label="Item Notes" style={{ minWidth: 400, flex: 1 }}>
                <TextArea
                  showCount
                  maxLength={2000}
                  allowClear
                  style={{ width: 400 }}
                  placeholder="Add additional information about what will be done to unit(s)."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                />
              </Form.Item>
              <Button type="primary" shape="circle" onClick={handleAddProduct} icon={<PlusOutlined />} size="large" />
            </Space>

            <Table
              columns={columns}
              dataSource={productOrderList.map((item, idx) => ({ ...item, index: idx }))}
              rowKey={(record) => record.index}
              pagination={false}
              style={{ marginTop: 16, width: '100%' }}
              bordered
              size="middle"
            />
          </Card>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, width: '100%' }}>
            <Button type="primary" htmlType="submit" size="large" style={{ minWidth: 120 }}>
              Save
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
